"use client";

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createDome } from "../../api/dome.api";

const CreateDome = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { hostData: host } = location.state || {};
  console.log("🚀 ~ CreateDome ~ host:", host);

  const [domeData, setDomeData] = useState({
    domeId: "",
    domeName: "",
    domeDescription: "",
    price: "",
    offerPercent: 0,
    location: "",
  });

  const [coverImage, setCoverImage] = useState(null);
  const [images, setImages] = useState([]);
  const [coverPreview, setCoverPreview] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Generate preview for cover image
  useEffect(() => {
    if (coverImage) {
      const url = URL.createObjectURL(coverImage);
      setCoverPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCoverPreview(null);
    }
  }, [coverImage]);

  // Generate preview URLs when additional images change
  useEffect(() => {
    const urls = images.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviewUrls(urls);

    // Cleanup URLs on unmount
    return () => urls.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [images]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDomeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) setCoverImage(file);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Append new images, don't overwrite
    setImages((prev) => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("hostRef", host._id);

      // Append cover image
      if (coverImage) formData.append("coverImage", coverImage);

      // Append other dome fields
      Object.entries(domeData).forEach(([key, value]) =>
        formData.append(key, value)
      );

      // Append additional images
      images.forEach((file) => formData.append("images", file));

      const response = await createDome(formData);
      console.log("✅ Dome created:", response);
      navigate("/hosts");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create dome");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2>
        Create Dome for:{" "}
        <span className="text-primary">
          {host?.companyDisplayName || host?.companyName}
        </span>
      </h2>

      <form onSubmit={handleSubmit} className="mt-3">
        <div className="mb-3">
          <label className="form-label">Dome ID</label>
          <input
            type="text"
            name="domeId"
            value={domeData.domeId}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Dome Name</label>
          <input
            type="text"
            name="domeName"
            value={domeData.domeName}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Dome Description</label>
          <textarea
            name="domeDescription"
            value={domeData.domeDescription}
            onChange={handleChange}
            className="form-control"
            rows={3}
          ></textarea>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Price</label>
            <input
              type="number"
              name="price"
              value={domeData.price}
              onChange={handleChange}
              className="form-control"
              min={0}
              required
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Offer Percent</label>
            <input
              type="number"
              name="offerPercent"
              value={domeData.offerPercent}
              onChange={handleChange}
              className="form-control"
              min={0}
              max={100}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Location</label>
          <input
            type="text"
            name="location"
            value={domeData.location}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* Cover Image */}
        <div className="mb-3">
          <label className="form-label">Cover Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleCoverChange}
            className="form-control"
          />
          {coverPreview && (
            <div className="mt-2" style={{ width: 200, height: 120 }}>
              <img
                src={coverPreview}
                alt="cover preview"
                className="img-fluid w-100 h-100"
                style={{ objectFit: "cover", borderRadius: 5 }}
              />
            </div>
          )}
        </div>

        {/* Additional Images */}
        <div className="mb-3">
          <label className="form-label">Additional Dome Images</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="form-control"
          />

          {previewUrls.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-3">
              {previewUrls.map(({ url }, idx) => (
                <div
                  key={idx}
                  className="border rounded position-relative overflow-hidden"
                  style={{
                    width: "120px",
                    height: "100px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <img
                    src={url}
                    alt={`preview-${idx}`}
                    className="img-fluid w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                    style={{
                      borderRadius: "50%",
                      fontSize: "0.8rem",
                      lineHeight: "1rem",
                    }}
                    onClick={() => handleRemoveImage(idx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Creating..." : "Create Dome"}
        </button>
      </form>
    </div>
  );
};

export default CreateDome;
